import React from 'react';
import { BlockMath } from 'react-katex';
import { Link } from 'react-router-dom';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@Utils';

import { NotionBlock } from '@Models';
import HeadingRenderer from './HeadingRenderer/HeadingRenderer';
import BulletedListItemRenderer from './BulletedListItemRenderer';
import ChildDatabaseTable from './ChildDatabaseTable';
import ColumnList from './ColumnList';
import { getCalloutStyles, groupChildrenByType } from './helpers';
import MediaRenderer from './MediaRenderer';
import MermaidRenderer from './MermaidRenderer';
import NumberedListItemRenderer from './NumberedListItemRenderer';
import RichText from './RichText';

interface NotionBlockRendererProps {
  block: NotionBlock;
  level?: number;
  defaultColor?: string;
  defaultTextSize?: string;
  variant?: 'documentation' | 'changelog';
  insideToggle?: boolean;
}

const NotionBlockRenderer = ({
  block,
  level = 0,
  defaultColor = 'text-black',
  defaultTextSize = 'text-base',
  variant = 'documentation',
  insideToggle = false,
}: NotionBlockRendererProps) => {
  // Render based on block type
  switch (block.type) {
    case 'heading_1':
      return (
        <HeadingRenderer
          block={block}
          level={1}
          defaultColor={defaultColor}
          variant={variant}
          renderChildren={() =>
            block.children?.map((child) => (
              <NotionBlockRenderer
                key={child.id}
                block={child}
                level={level + 1}
                variant={variant}
                insideToggle={block.content.is_toggleable}
              />
            ))
          }
        />
      );

    case 'heading_2':
      return (
        <HeadingRenderer
          block={block}
          level={2}
          defaultColor={defaultColor}
          variant={variant}
          renderChildren={() =>
            block.children?.map((child) => (
              <NotionBlockRenderer
                key={child.id}
                block={child}
                level={level + 1}
                variant={variant}
                insideToggle={block.content.is_toggleable}
              />
            ))
          }
        />
      );

    case 'heading_3':
      return (
        <HeadingRenderer
          block={block}
          level={3}
          defaultColor={defaultColor}
          variant={variant}
          renderChildren={() =>
            block.children?.map((child) => (
              <NotionBlockRenderer
                key={child.id}
                block={child}
                level={level + 1}
                variant={variant}
                insideToggle={block.content.is_toggleable}
              />
            ))
          }
        />
      );

    case 'paragraph':
      return (
        <div className="mb-2">
          <p className={cn(defaultTextSize, 'leading-relaxed')}>
            <RichText segments={block.content.rich_content || []} defaultColor={defaultColor} />
          </p>
          {block.children &&
            block.children.map((child) => (
              <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
            ))}
        </div>
      );

    case 'bulleted_list_item':
      return (
        <BulletedListItemRenderer
          block={block}
          level={level}
          defaultColor={defaultColor}
          defaultTextSize={defaultTextSize}
          renderChild={(childBlock, childLevel) => (
            <NotionBlockRenderer
              key={childBlock.id}
              block={childBlock}
              level={childLevel}
              defaultColor={defaultColor}
              defaultTextSize={defaultTextSize}
            />
          )}
        />
      );

    case 'numbered_list_item':
      return (
        <NumberedListItemRenderer
          block={block}
          level={level}
          defaultColor={defaultColor}
          defaultTextSize={defaultTextSize}
          renderChild={(childBlock, childLevel) => (
            <NotionBlockRenderer
              key={childBlock.id}
              block={childBlock}
              level={childLevel}
              defaultColor={defaultColor}
              defaultTextSize={defaultTextSize}
            />
          )}
        />
      );

    case 'to_do':
      return (
        <div className="my-2 flex items-start gap-2">
          <input
            type="checkbox"
            checked={block.content.checked || false}
            readOnly
            className="mt-1 cursor-default pointer-events-none"
          />
          <div className="flex-1">
            <span className={cn(defaultTextSize, block.content.checked && 'line-through opacity-60')}>
              <RichText segments={block.content.rich_content || []} defaultColor={defaultColor} />
            </span>
            {block.children &&
              block.children.map((child) => (
                <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
              ))}
          </div>
        </div>
      );

    case 'code': {
      // Check if it's a mermaid diagram
      if (block.content.language === 'mermaid') {
        return (
          <div className="mb-4">
            <MermaidRenderer code={block.content.text || ''} />
            {block.content.caption && (
              <p className="text-sm mt-2 text-center max-w-2xl">
                <RichText segments={block.content.caption.rich_content || []} defaultColor="text-gray-700" />
              </p>
            )}
            {block.children &&
              block.children.map((child) => (
                <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
              ))}
          </div>
        );
      }

      // Regular code block
      return (
        <div className="mb-4">
          <pre className="bg-gray-300 text-red-700 p-6 rounded-lg overflow-x-auto">
            <code className="text-sm font-['Fira_Code']">{block.content.text}</code>
          </pre>
          {block.content.caption && (
            <p className="text-sm mt-2 text-center max-w-2xl">
              <RichText segments={block.content.caption.rich_content || []} defaultColor="text-gray-700" />
            </p>
          )}
          {block.children &&
            block.children.map((child) => (
              <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
            ))}
        </div>
      );
    }

    case 'quote':
      return (
        <div className="mb-4 flex">
          <div className="w-1 bg-gray-900" />
          <blockquote className="flex-1 py-2 m-0 mx-5">
            <RichText segments={block.content.rich_content || []} defaultColor="text-gray-900" />
          </blockquote>
          {block.children && (
            <div className="ml-6">
              {block.children.map((child) => (
                <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
              ))}
            </div>
          )}
        </div>
      );

    case 'callout': {
      const calloutStyles = getCalloutStyles(block.color);

      return (
        <div
          className={cn(
            'mb-4 rounded-lg p-4 flex items-center gap-3',
            calloutStyles,
            block.color === 'default' && 'outline',
          )}>
          {block.content.icon && block.content.icon.type === 'emoji' && (
            <span className="text-2xl">{block.content.icon.emoji}</span>
          )}
          <div className="flex-1 flex flex-col gap-1">
            <RichText segments={block.content.rich_content || []} defaultColor={defaultColor} />
            {block.children &&
              block.children.map((child) => (
                <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
              ))}
          </div>
        </div>
      );
    }

    case 'toggle':
      return (
        <details className="mb-3 rounded-lg p-3">
          <summary className="cursor-pointer font-medium">
            <RichText segments={block.content.rich_content || []} defaultColor={defaultColor} />
          </summary>
          {block.children && (
            <div className="mt-3 pl-4">
              {block.children.map((child) => (
                <NotionBlockRenderer key={child.id} block={child} level={level + 1} variant={variant} />
              ))}
            </div>
          )}
        </details>
      );

    case 'divider':
      return <hr className="my-6 border-gray-600" />;

    case 'equation':
      return (
        <div className="my-4 flex justify-center items-center">
          <div className="px-6 py-4 rounded-lg overflow-x-auto [&_.katex-html]:!hidden [&_.katex]:text-xl">
            <BlockMath math={block.content.expression || ''} />
          </div>
        </div>
      );

    case 'image':
      return <MediaRenderer block={block} type="image" />;

    case 'video':
      return <MediaRenderer block={block} type="video" />;

    case 'file':
      return (
        <div className="my-1">
          <a
            href={block.content.url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="underline decoration-gray-700 hover:decoration-white inline-flex items-center gap-2 px-2 py-1 hover:bg-gray-400 rounded transition-colors duration-150 group">
            <span>📎</span>
            <span className="text-gray-700 group-hover:text-white font-bold">
              {block.content.name_file || 'Download File'}
            </span>
          </a>
        </div>
      );

    case 'embed':
      return <MediaRenderer block={block} type="embed" />;

    case 'table': {
      const rows = block.children?.filter((child) => child.type === 'table_row') || [];
      const hasColumnHeader = block.content.has_column_header;
      const hasRowHeader = block.content.has_row_header;

      return (
        <div className="my-6">
          <div className="overflow-auto max-h-[600px]">
            <table className="min-w-full">
              {hasColumnHeader && rows.length > 0 && (
                <thead className="sticky top-0 z-10 bg-gray-200">
                  <tr>
                    {rows[0].content.cells?.map((cell, cellIndex) => (
                      <th
                        key={String(cell) + String(cellIndex)}
                        className={`px-4 py-2.5 text-left text-sm font-medium text-gray-500 border-b border-gray-200 ${
                          cellIndex !== 0 ? 'border-l border-gray-200' : ''
                        }`}>
                        <RichText segments={cell.rich_content || []} defaultColor="text-gray-500" />
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.slice(hasColumnHeader ? 1 : 0).map((rowBlock, rowIndex, arr) => (
                  <tr key={rowBlock.id} className="hover:bg-gray-200/50 transition-colors">
                    {rowBlock.content.cells?.map((cell, cellIndex) => {
                      const isRowHeader = hasRowHeader && cellIndex === 0;
                      return isRowHeader ? (
                        <th
                          key={String(cell) + String(cellIndex)}
                          className={`px-4 py-3 text-left text-sm font-medium text-gray-500 ${
                            rowIndex !== arr.length - 1 ? 'border-b border-gray-200' : ''
                          }`}>
                          <RichText segments={cell.rich_content || []} defaultColor="text-gray-500" />
                        </th>
                      ) : (
                        <td
                          key={String(cell) + String(cellIndex)}
                          className={`px-4 py-3 text-sm text-gray-900 ${
                            rowIndex !== arr.length - 1 ? 'border-b border-gray-200' : ''
                          } ${cellIndex !== 0 ? 'border-l border-gray-200' : ''}`}>
                          <RichText segments={cell.rich_content || []} defaultColor={defaultColor} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    case 'child_page':
    case 'link_to_page': {
      const isDatabase = block.content.object_type === 'database';
      const icon = isDatabase ? '🗄️' : '📄';
      const linkPath = isDatabase
        ? `/documentation/database/${block.content.page_id ?? block.id}`
        : `/documentation/${block.content.page_id ?? block.id}`;

      return (
        <div className="my-3">
          <Link
            to={linkPath}
            className="underline decoration-gray-700 hover:decoration-white inline-flex items-center gap-2 px-2 py-1 hover:bg-gray-400 rounded transition-colors duration-150 group">
            <span>{icon}</span>
            <span className="text-gray-700 group-hover:text-white font-bold">{block.content.title}</span>
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              className="text-xs text-gray-500 group-hover:text-gray-700 ml-1"
            />
          </Link>
        </div>
      );
    }

    case 'child_database':
      // If inside a toggle, render as a link instead of the full table
      if (insideToggle) {
        return (
          <div className="my-1">
            <Link
              to={`/documentation/database/${block.content.database_id ?? block.id}`}
              className="underline decoration-gray-700 hover:decoration-white inline-flex items-center gap-2 px-2 py-1 hover:bg-gray-400 rounded transition-colors duration-150 group">
              <span className="">🗄️</span>
              <span className="text-gray-700 group-hover:text-white font-bold">
                {block.content.title || 'Database'}
              </span>
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="text-xs text-gray-500 group-hover:text-gray-700 ml-1"
              />
            </Link>
          </div>
        );
      }
      // Normal rendering: show full table
      return <ChildDatabaseTable block={block} />;

    case 'column_list':
      return (
        <ColumnList
          columns={block.children || []}
          renderColumn={(column) => {
            const content = column.children || [];
            const grouped = groupChildrenByType(content);

            return (
              <>
                {grouped.map((group, idx) => {
                  if (group.type === 'numbered_list_item') {
                    return (
                      <ol key={group.type + String(idx)} className="list-decimal pl-5 mb-2">
                        {group.items.map((item) => (
                          <NotionBlockRenderer
                            key={item.id}
                            block={item}
                            level={0}
                            defaultColor={defaultColor}
                            defaultTextSize={defaultTextSize}
                            variant={variant}
                          />
                        ))}
                      </ol>
                    );
                  }
                  if (group.type === 'bulleted_list_item') {
                    return (
                      <ul key={group.type + String(idx)} className="list-disc pl-5 mb-2">
                        {group.items.map((item) => (
                          <NotionBlockRenderer
                            key={item.id}
                            block={item}
                            level={0}
                            defaultColor={defaultColor}
                            defaultTextSize={defaultTextSize}
                            variant={variant}
                          />
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <NotionBlockRenderer
                      key={group.items[0].id}
                      block={group.items[0]}
                      level={level}
                      defaultColor={defaultColor}
                      defaultTextSize={defaultTextSize}
                      variant={variant}
                    />
                  );
                })}
              </>
            );
          }}
        />
      );

    default:
      return null;
  }
};

export default NotionBlockRenderer;
